import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl md:text-3xl font-bold text-[#B8860B] mb-3">{title}</h2>
    <div className="text-[#F4F1EA]/85 leading-relaxed space-y-3">{children}</div>
  </section>
);

export const Terms = () => {
  const lastUpdated = 'February 5, 2026';
  return (
    <div className="min-h-screen pt-20 bg-[#050814]">
      <section className="relative h-56 flex items-center justify-center overflow-hidden border-b border-[#B8860B]/20">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(5, 8, 20, 0.85), rgba(5, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1656740976671-3f4e8bab24c3?auto=format&fit=crop&w=2000&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="container mx-auto px-4 z-10 text-center text-[#F4F1EA]">
          <h1 className="text-4xl md:text-5xl font-bold mb-2">Terms &amp; Conditions</h1>
          <p className="text-sm text-[#B8860B]">Last updated: {lastUpdated}</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12 max-w-3xl text-[#F4F1EA]">
        <p className="text-[#F4F1EA]/85 mb-10 leading-relaxed">
          Welcome to Creek Bottom Ranch &amp; Resort ("Creek Bottom," "we," "us"). These Terms &amp;
          Conditions govern your use of creekbottomranch.com and any reservation, equipment rental,
          or service you book with us. By using our site or making a reservation you accept these terms.
        </p>

        <Section title="1. Reservations & Payment">
          <ul className="list-disc pl-6 space-y-1">
            <li>All reservations require full payment at the time of booking via Stripe.</li>
            <li>Pricing is in U.S. dollars and includes all applicable taxes unless otherwise noted.</li>
            <li>You must be at least 21 years old to make a reservation. Creek Bottom Ranch &amp; Resort is an adults-only property.</li>
            <li>Check-in: 2:00 PM. Check-out: 11:00 AM.</li>
            <li>A valid government-issued ID is required at check-in.</li>
          </ul>
        </Section>

        <Section title="2. Cancellation Policy">
          <ul className="list-disc pl-6 space-y-1">
            <li>Free cancellation up to <strong>48 hours</strong> before your check-in date for a full refund.</li>
            <li>Cancellations within 48 hours of check-in are subject to a one-night fee.</li>
            <li>No-shows are charged the full reservation amount.</li>
            <li>Refunds are issued to the original payment method within 5–10 business days.</li>
          </ul>
        </Section>

        <Section title="3. Equipment Rentals">
          <ul className="list-disc pl-6 space-y-1">
            <li>Renter must be 21 or older with a valid driver's license.</li>
            <li>A refundable security deposit (where applicable) is collected at the time of booking and returned upon equipment return in good condition.</li>
            <li>Renter is responsible for damage, loss, or theft of rental equipment, up to the full replacement cost.</li>
            <li>Weekly rate covers a 7-day period; partial-week days are billed at the daily rate.</li>
            <li>Equipment must be returned in the same condition (fueled, cleaned) it was issued. Late returns incur additional daily fees.</li>
          </ul>
        </Section>

        <Section title="4. SMS / Text Message Terms">
          <p>
            By providing your phone number and checking the SMS consent box, you agree to receive
            transactional and operational text messages from Creek Bottom Ranch &amp; Resort via
            <strong> Twilio</strong>.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Program:</strong> Booking confirmations, check-in reminders, equipment rental updates, and important operational messages.</li>
            <li><strong>Frequency:</strong> Variable — typically 1 to 5 messages per booking or rental.</li>
            <li><strong>Cost:</strong> Message and data rates may apply. Creek Bottom Ranch &amp; Resort does not charge for messages.</li>
            <li><strong>Opt-out:</strong> Reply <strong>STOP</strong> to any message to unsubscribe at any time. Reply <strong>HELP</strong> for assistance, or email info@creekbottomranch.com.</li>
            <li><strong>Carriers:</strong> Mobile carriers are not liable for delayed or undelivered messages.</li>
            <li>Phone numbers and SMS opt-in data are <strong>never</strong> shared or sold to third parties for marketing.</li>
          </ul>
          <p>
            For full details, see our{' '}
            <Link to="/privacy" className="text-[#B8860B] hover:underline">
              Privacy Policy
            </Link>
            .
          </p>
        </Section>

        <Section title="5. Property Rules & Conduct">
          <ul className="list-disc pl-6 space-y-1">
            <li>Quiet hours: 10:00 PM – 8:00 AM.</li>
            <li>Pets are welcome (max 2 per site), must be leashed at all times, and owners are responsible for cleanup.</li>
            <li>Open fires only in designated fire rings; check posted burn-ban status.</li>
            <li>No firearms, illegal substances, or fireworks on premises.</li>
            <li>Speed limit on all ranch roads: 10 mph.</li>
            <li>Guests are responsible for their visitors' conduct.</li>
            <li>Creek Bottom Ranch &amp; Resort reserves the right to remove any guest violating these rules without refund.</li>
          </ul>
        </Section>

        <Section title="6. Liability & Assumption of Risk">
          <p>
            Outdoor recreation and equipment use carry inherent risks. By making a reservation or
            renting equipment you acknowledge and assume these risks. Creek Bottom Ranch &amp;
            Resort, its owners, employees, and contractors are not liable for personal injury,
            loss, or damage to property except where caused by gross negligence or willful misconduct.
          </p>
          <p>
            ATV, hiking, and off-road activities are undertaken at your own risk. Helmets and
            appropriate safety gear are recommended at all times.
          </p>
        </Section>

        <Section title="7. Photography & Marketing">
          <p>
            Creek Bottom Ranch &amp; Resort may take photographs of property and amenities for
            marketing. Guests appearing incidentally in such photos consent to their reasonable use.
            If you do not wish to be photographed, please notify staff at check-in.
          </p>
        </Section>

        <Section title="8. Force Majeure">
          <p>
            We are not liable for cancellations or interruptions caused by events beyond our
            reasonable control (severe weather, natural disasters, public-health emergencies,
            government action). In such cases we will work in good faith to reschedule or refund
            affected reservations.
          </p>
        </Section>

        <Section title="9. Intellectual Property">
          <p>
            All site content, photography, branding, and copy belong to Creek Bottom Ranch &amp;
            Resort. You may not reproduce or redistribute without written permission.
          </p>
        </Section>

        <Section title="10. Governing Law">
          <p>
            These Terms are governed by the laws of the State of Texas, without regard to conflict
            of law principles. Any dispute will be resolved in the courts of the county where the
            ranch is located.
          </p>
        </Section>

        <Section title="11. Changes to These Terms">
          <p>
            We may update these Terms from time to time. The current version posted on this page
            governs all reservations and rentals.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            <strong>Creek Bottom Ranch &amp; Resort</strong>
            <br />
            2724 Private Road 8160
            <br />
            Lovelady, TX 75851
            <br />
            Phone:{' '}
            <a className="text-[#B8860B] hover:underline" href="tel:8327140101">
              (832) 714-0101
            </a>
            <br />
            Email:{' '}
            <a className="text-[#B8860B] hover:underline" href="mailto:info@creekbottomranch.com">
              info@creekbottomranch.com
            </a>
          </p>
        </Section>

        <p className="text-sm text-[#F4F1EA]/60 mt-12 border-t border-[#B8860B]/20 pt-6">
          See also our{' '}
          <Link to="/privacy" className="text-[#B8860B] hover:underline">
            Privacy Policy
          </Link>
          .
        </p>
      </div>
    </div>
  );
};

export default Terms;
